import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import sadCatImg from '@/shared/assets/images/sad-cat.png';
import { useMedia } from '@/shared/hooks';
import { Layout, MobileLayout, Result, Spinner } from '@/shared/ui';

import { ConfirmModal } from '@/entities/modal';

import { Footer } from '@/features/footer';

import { Header, MobileHeader } from '@/widgets/Header';

import { usePatientRecordingsPage } from './hooks';
import styles from './PatientRecordings.module.scss';
import {
  HiddenRecordings,
  PatientRecordingsHeader,
  RecordingsTimeline,
} from './ui';

export const PatientRecordings: FC = () => {
  const { formatMessage } = useIntl();
  const { patientID = '' } = useParams<{ patientID: string }>();
  const { isMobile } = useMedia();

  const {
    groups,
    hiddenGroups,
    hiddenCount,
    visibleTotal,
    isLoaded,
    hasLoadError,
    isEmpty,
    showHiddenSection,
    hideMode,
    userTimezone,
    funding,
    canStartRecording,
    isStartRecordingDisabled,
    isStorageBlocked,
    isConsultationStale,
    rowActions,
    handleStartRecording,
  } = usePatientRecordingsPage(patientID);

  if (!isLoaded && !hasLoadError) {
    return (
      <div className={styles.spinnerWrapper}>
        <Spinner className={styles.spinner} size={96} />
      </div>
    );
  }

  const header = (
    <PatientRecordingsHeader
      total={visibleTotal}
      funding={funding}
      isMobile={isMobile}
      canStartRecording={canStartRecording}
      isStartRecordingDisabled={isStartRecordingDisabled}
      isStorageBlocked={isStorageBlocked}
      onStartRecording={handleStartRecording}
    />
  );

  const sadCat = (
    <img
      src={sadCatImg}
      width={130}
      height={130}
      alt={formatMessage({
        id: 'imgAltText.sadCat',
        defaultMessage: 'Sad cat',
      })}
    />
  );

  const content = hasLoadError ? (
    <Result
      className={styles.emptyResult}
      icon={sadCat}
      text={formatMessage({
        id: 'patientRecordings.loadError',
        defaultMessage: 'Failed to load recordings',
      })}
    />
  ) : isEmpty ? (
    <Result
      className={styles.emptyResult}
      icon={sadCat}
      text={formatMessage({
        id: 'patientRecordings.emptyResult.text',
        defaultMessage: 'There are no recordings yet',
      })}
    />
  ) : (
    <RecordingsTimeline
      groups={groups}
      userTimezone={userTimezone}
      hideMode={hideMode}
      isConsultationStale={isConsultationStale}
      {...rowActions}
    />
  );

  const hidden = showHiddenSection && (
    <HiddenRecordings
      count={hiddenCount}
      groups={hiddenGroups}
      userTimezone={userTimezone}
      isConsultationStale={isConsultationStale}
      {...rowActions}
    />
  );

  if (isMobile) {
    return (
      <>
        <MobileLayout>
          <MobileHeader hideBreadcrumbs />

          <MobileLayout.Main>
            <div
              className={styles.mobileContainer}
              data-section="patient-recordings"
            >
              {header}
              {content}
              {hidden}
            </div>
          </MobileLayout.Main>
        </MobileLayout>
        <ConfirmModal />
      </>
    );
  }

  return (
    <>
      <Layout>
        <Layout.Header>
          <Header />
        </Layout.Header>

        <Layout.Content>
          <Layout.Main>
            <div className={styles.container} data-section="patient-recordings">
              {header}
              {content}
              {hidden}
            </div>
          </Layout.Main>
        </Layout.Content>

        <Layout.Footer>
          <Footer />
        </Layout.Footer>
      </Layout>
      <ConfirmModal />
    </>
  );
};
